<template>
  <v-container>
    <ContentHeader
      header-icon="mdi-chart-line"
      header-title="Reports"
      back-navigation
      back-navigation-href="/admin"
      back-navigation-text="To Old Admin"
    >
    </ContentHeader>
    <PtSheet class="admin-reports">
      <v-alert type="info">
        Select a report from the dropdown and click 'Generate Report' to run the
        report. If the report requires parameters, a dialog will be presented to
        enter them.<br />
        While a report is generating or waiting to start, the table will
        automatically refresh every 5 seconds.<br />
        When the report finishes, a green 'Download' icon will appear in the
        right column that you can click to download the report.<br />
        Only the most recent 100 reports are shown.
      </v-alert>
      <v-row no-gutters>
        <v-col cols="auto">
          <v-select
            outlined
            dense
            class="report-select"
            :items="reports"
            item-text="name"
            item-value="identifier"
            v-model="selectedReport"
            return-object
            label="Select Report"
          ></v-select>
        </v-col>
        <v-col cols="auto">
          <v-btn
            class="ml-4"
            outlined
            color="primary"
            :loading="loading"
            @click="onClickGenerate"
          >
            Generate Report
          </v-btn>
        </v-col>
      </v-row>
      <v-data-table
        :headers="headers"
        :items="generatedReports"
        :disable-pagination="true"
        hide-default-footer
        :loading="tableDataLoading"
        loading-text="Loading... Please wait"
        :key="tableRefreshKey"
      >
        <template v-slot:item.startTime="{ item }">
          {{ formatDateTime(item.startTime) }}
        </template>
        <template v-slot:item.endTime="{ item }">
          {{ formatDateTime(item.endTime) }}
        </template>
        <template v-slot:item.generationTime="{ item }">
          {{ calculateGenerationTime(item.startTime, item.endTime) }}
        </template>
        <template v-slot:item.parameters="{ item }">
          <div v-for="(value, key) in item.parameters" :key="key">
            {{ getParameterName(key, item.identifier) }}: {{ value }}
          </div>
        </template>
        <template v-slot:item.generationStatus="{ item }">
          {{ formatGenerationStatus(item.generationStatus) }}
        </template>
        <template v-slot:item.reportFileId="{ item }">
          <v-icon v-if="item.generationStatus === 'NOT_STARTED'"></v-icon>
          <v-progress-circular
            v-if="item.generationStatus === 'GENERATING'"
            indeterminate
            color="colorNeutralsNavy200"
            size="20"
            width="3"
          ></v-progress-circular>
          <v-tooltip v-if="item.generationStatus === 'ERROR'" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on">mdi-alert-circle-outline</v-icon>
            </template>
            <span>{{ item.notes }}</span>
          </v-tooltip>
          <v-tooltip top v-if="item.notes">
            <template v-slot:activator="{ on, attrs }">
              <a
                v-bind="attrs"
                v-on="on"
                v-if="item.generationStatus === 'FINISHED'"
                :href="item.reportFileLink.replace('/services', '')"
              >
                <v-icon color="colorSemanticStateGreen500">mdi-download</v-icon>
              </a>
            </template>
            <span>{{ item.notes }}</span>
          </v-tooltip>
          <a
            v-else-if="item.generationStatus === 'FINISHED'"
            :href="item.reportFileLink.replace('/services', '')"
          >
            <v-icon color="colorSemanticStateGreen500">mdi-download</v-icon>
          </a>
        </template>
      </v-data-table>

      <v-dialog v-if="dialog" v-model="dialog" persistent max-width="600px">
        <v-card>
          <v-card-title>
            <span class="text-h5"
              >{{ selectedReport.name }} Report Parameters</span
            >
          </v-card-title>

          <v-card-text>
            <parameter-input
              v-for="(param, index) in selectedReport.parameters"
              :key="`param-${index}`"
              :param="param"
            />
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue darken-1" text @click="dialog = false"
              >Cancel</v-btn
            >
            <v-btn color="blue darken-1" text @click="generateParameteredReport"
              >Generate Report</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
    </PtSheet>
  </v-container>
</template>

<script>
import { get, post } from "@/services/request";
import { asyncReportsAPI } from "@/services/api";
import PtSheet from "@/components/PtSheet.vue";
import ContentHeader from "@/views/ContentHeader.vue";
import { ADMIN_REPORTS } from "@/services/constants";
import hasNotifications from "@/mixins/has-notifications";
import moment from "moment";
import ParameterInput from "@/components/ParameterInput.vue";
require("moment-duration-format");

const defaultReportState = {
  id: null,
  name: "",
  identifier: "",
  startTime: null,
  endTime: null,
  requestingUser: null,
  reportFileId: null,
  reportFileLink: "",
  generationStatus: "",
  notes: "",
  parameters: {}
};

export default {
  name: "AdminReports",
  mixins: [hasNotifications],
  components: {
    ParameterInput,
    ContentHeader,
    PtSheet
  },

  data: () => ({
    refreshIntervalId: null,
    tableRefreshKey: 0,
    loading: false,
    tableDataLoading: false,
    reports: ADMIN_REPORTS,
    selectedReport: null,
    dialog: false,
    newReport: { ...defaultReportState },
    headers: [
      { text: "Report Name", value: "name" },
      { text: "Start Time", value: "startTime" },
      { text: "End Time", value: "endTime" },
      { text: "Generation Time", value: "generationTime" },
      { text: "User", value: "requestingUser" },
      { text: "Parameters", value: "parameters" },
      { text: "Status", value: "generationStatus" },
      { text: "Download", value: "reportFileId" }
    ],
    generatedReports: []
  }),
  created() {
    this.loadTableData();
  },
  beforeDestroy() {
    if (this.refreshIntervalId) {
      clearInterval(this.refreshIntervalId);
      this.refreshIntervalId = null;
    }
  },
  methods: {
    // Intentionally keeping this method here in case we go back to running this report synchronously
    // downloadSIE() {
    //   this.loading = true;
    //   getCSV(salesforceInvoiceAPI)
    //     .then(response => {
    //       const disposition = response.headers["content-disposition"];
    //       const filename = decodeURI(disposition.match(/filename="?(.*)"?/)[1]);
    //
    //       saveFile(response.data, filename);
    //     })
    //     .catch(e =>
    //       console.error("Error downloading Salesforce Invoice Export", e)
    //     )
    //     .finally(() => {
    //       this.loading = false;
    //     });
    // },
    loadTableData() {
      if (this.tableDataLoading) {
        // If a request is already in progress, don't start another one.
        // This check is necessary since we're automatically calling this method on an interval while reports are
        // generating.
        return;
      }

      // If we reached here, it means no request is in progress
      this.tableDataLoading = true;
      get(asyncReportsAPI)
        .then(response => {
          if (response && response.data) {
            this.generatedReports = response.data;

            //Download link wasn't showing up when reports finish, so force reload
            this.tableRefreshKey += 1;

            // check report statuses
            const shouldRefresh = this.generatedReports.some(
              report =>
                report.generationStatus === "NOT_STARTED" ||
                report.generationStatus === "GENERATING"
            );

            if (shouldRefresh && !this.refreshIntervalId) {
              // If refreshing is needed and there's no ongoing interval
              // Start refreshing every 5 seconds
              this.refreshIntervalId = setInterval(this.loadTableData, 5000);
            } else if (!shouldRefresh && this.refreshIntervalId) {
              // If no need to refresh but an interval is active
              // Clear the interval as all reports have completed generation
              clearInterval(this.refreshIntervalId);
              this.refreshIntervalId = null;
            }
          }
        })
        .catch(error => {
          const errorMessage = "Error loading generated reports";
          console.error(errorMessage, error);

          this.showMessage({
            mode: "error",
            text: errorMessage
          });
        })
        .finally(() => {
          this.tableDataLoading = false;
        });
    },
    onClickGenerate() {
      if (this.selectedReport) {
        // if the report has parameters, show the dialog
        if (
          this.selectedReport.parameters &&
          this.selectedReport.parameters.length > 0
        ) {
          this.dialog = true;
        } else {
          // otherwise, generate the report immediately
          this.generateReport();
        }
      } else {
        this.showMessage({
          text: "No report selected",
          mode: "warning"
        });
      }
    },
    generateReport() {
      this.loading = true;
      console.log(`Generating report: ${this.selectedReport.identifier}`);
      this.newReport.identifier = this.selectedReport.identifier;
      this.newReport.name = this.selectedReport.name;
      post(asyncReportsAPI, this.newReport)
        .then(response => {
          if (response) {
            this.showMessage(
              `Generating '` +
                this.selectedReport.name +
                `'. The table below will update when the report is done.`
            );
          }
        })
        .catch(() =>
          this.showMessage({
            text:
              "Error generating report, please try again or contact us for support",
            mode: "error"
          })
        )
        .finally(() => {
          this.newReport = { ...defaultReportState };
          this.loadTableData();
          this.loading = false;
        });
    },
    generateParameteredReport() {
      console.log("Parameter values:");
      for (let param of this.selectedReport.parameters) {
        console.log(`${param.identifier}: ${param.value}`);
      }
      this.newReport.parameters = this.selectedReport.parameters.reduce(
        (obj, item) => {
          obj[item.identifier] = item.value;
          return obj;
        },
        {}
      );
      this.generateReport();

      // clear parameter inputs and close dialog
      this.selectedReport.parameters.forEach(parameter => {
        parameter.value = "";
      });
      this.dialog = false;
    },
    calculateGenerationTime(startTime, endTime) {
      if ((!startTime || !startTime.length) && (!endTime || !endTime.length)) {
        return "";
      }

      const start = moment(startTime);
      const stillGenerating = !endTime || endTime.length === 0;
      const end = stillGenerating ? moment() : moment(endTime);
      const duration = moment.duration(end.diff(start));

      return duration.format("h[h] m[m] s[s]");
    },
    getParameterName(identifier, reportIdentifier) {
      const report = ADMIN_REPORTS.find(r => r.identifier === reportIdentifier);
      if (report && report.parameters) {
        const param = report.parameters.find(p => p.identifier === identifier);
        return param ? param.name : identifier;
      } else {
        return identifier;
      }
    },
    formatDateTime(dateTime) {
      if (!dateTime || dateTime.length === 0) {
        return "";
      }

      const date = moment(dateTime);

      let formattedDate;

      if (!date.isSame(moment(), "year")) {
        // If the date is not from this year, display the full date
        formattedDate = date.format("MMM DD, YYYY h:mm A");
      } else if (!date.isSame(moment(), "day")) {
        // If the date is not from today, display month, day and time
        formattedDate = date.format("MMM DD h:mm A");
      } else {
        // if the date is from today, just display time
        formattedDate = date.format("h:mm A");
      }

      return formattedDate;
    },
    formatGenerationStatus(status) {
      switch (status) {
        case "NOT_STARTED":
          return "Awaiting Start";
        case "GENERATING":
          return "Generating";
        case "FINISHED":
          return "Done";
        case "ERROR":
          return "Error";
        default:
          return "Unknown";
      }
    }
  }
};
</script>

<style scoped lang="scss">
.report-select {
  width: 300px;
}
.v-application .v-alert {
  color: white;
  font-size: 16px;
  font-weight: 300;

  &.theme--dark {
    a {
      color: white;
    }
  }
}
.v-alert__icon.v-icon {
  align-self: unset !important;
}
</style>
